import type { ChangeEvent } from 'react';
import { useCallback, useEffect } from 'react';

import { useBoolean } from '@/_shared';

import type { EmailFormSchema, EmailsProps } from '../shared';
import { useFormDefinition } from '../shared';

export type Props = Omit<EmailsProps, 'value' | 'onChange'> & {
  value: EmailFormSchema;
  onChange: (v: EmailFormSchema) => void;
};

export function useUpdateEmailItem({ onChange, value }: Props) {
  const [isPending, setIsPending] = useBoolean();
  const { id, pivot } = value;
  const form = useFormDefinition({ ...value });
  const [localIntent, localDefault] = form.watch(['intent', 'pivot.default']);

  const validateForm = useCallback(async () => {
    const newV = form.getValues();
    const res = await form.trigger();
    if (!res) {
      // Return undefined to differentiate between API error and form validation error
      return undefined;
    }
    return newV;
  }, [form]);

  const onSubmit = useCallback(async () => {
    setIsPending.setTrue();
    const newV = await validateForm();

    if (newV) {
      if (newV.intent === 'create') {
        onChange({ ...newV });
      } else {
        onChange({ ...newV, intent: form.formState.defaultValues?.email === newV.email ? undefined : 'update' });
      }
    }
    setIsPending.setFalse();
    return Boolean(newV);
  }, [form.formState.defaultValues?.email, onChange, setIsPending, validateForm]);

  const handleOnChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      form.setValue('email', target.value, { shouldDirty: true });
      onChange({ ...form.getValues(), email: target.value });
    },
    [form, onChange]
  );

  useEffect(() => {
    const { setDirtyHelper, intent } = form.getValues();
    if (!setDirtyHelper && intent === 'create') {
      form.setValue('setDirtyHelper', true, { shouldDirty: true });
    }

    form.setValue('pivot.default', pivot.default, { shouldDirty: true });
  }, [form, onChange, pivot.default]);

  useEffect(() => {
    if (localDefault === 1) {
      onChange(form.getValues());
    }
    if (localIntent === 'delete') {
      onChange(form.getValues());
    }
  }, [form, localIntent, localDefault, onChange]);

  return { id, pivot, form, onSubmit, handleOnChange, isPending };
}
